import { Box, Stack } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { NumericField } from "../../../positions-and-orders/controls/NumericField";
import { StepChangeSelector } from "../../../positions-and-orders/controls/StepChangeSelector";
import { ChangingDirectionType } from "../../../positions-and-orders/types";

const SELECTOR_ZOOM = 0.87;

type NumericFieldWithStepSelectorProps = {
    id?: string;
    label: string;
    value: number;
    onValueChange: (newValue: number) => void;
    formatter?: (value: number) => string;
    numericFieldStep?: number | "any";
    disabled?: boolean;
    initialSelectedStep: number;
    stepOptions: number[];
    buttonContent: string;
    tooltipSubject: string;
    selectorWidth?: number;
    error?: boolean;
    helperText?: React.ReactNode;
};

export function NumericFieldWithStepSelector({
    id,
    label,
    value,
    onValueChange,
    formatter,
    numericFieldStep,
    disabled = false,
    initialSelectedStep,
    stepOptions,
    buttonContent,
    tooltipSubject,
    selectorWidth,
    error = false,
    helperText,
}: NumericFieldWithStepSelectorProps) {
    const selectorRef = useRef<HTMLDivElement>(null);
    const [actualSelectorWidth, setActualSelectorWidth] = useState<number | null>(null);
    const [selectedStep, setSelectedStep] = useState(initialSelectedStep);

    useLayoutEffect(() => {
        const element = selectorRef.current;

        if (!element) {
            return;
        }

        let animationFrameId: number | null = null;

        const measureWidth = () => {
            const measuredWidth = Math.ceil(
                element.getBoundingClientRect().width
            );

            setActualSelectorWidth(currentWidth =>
                currentWidth === measuredWidth
                    ? currentWidth
                    : measuredWidth
            );
        };

        const updateWidth = () => {
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
            }

            animationFrameId = requestAnimationFrame(measureWidth);
        };

        measureWidth();

        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(element);

        window.addEventListener("resize", updateWidth);

        return () => {
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
            }

            resizeObserver.disconnect();
            window.removeEventListener("resize", updateWidth);
        };
    }, []);

    const handleStepChange = (
        direction: ChangingDirectionType,
        step: number
    ): void => {
        onValueChange(
            direction === ChangingDirectionType.Next
                ? value + step
                : value - step
        );
    };

    const componentWidth = actualSelectorWidth !== null && actualSelectorWidth > 0
        ? actualSelectorWidth - 1
        : undefined;

    return (
        <Stack
            direction="column"
            spacing={0.75}
            sx={{
                width:
                    componentWidth !== undefined
                        ? `${componentWidth}px`
                        : "fit-content",
            }}
        >
            <NumericField
                id={id}
                name={id}
                label={label}
                value={value}
                onValueChange={onValueChange}
                formatter={formatter}
                width={componentWidth}
                step={numericFieldStep}
                disabled={disabled}
                error={error}
                helperText={helperText}
            />

            <Box
                sx={{
                    width: "max-content",
                    zoom: SELECTOR_ZOOM,
                }}
            >
                <Box
                    ref={selectorRef}
                    sx={{
                        display: "inline-flex",
                        width: "max-content",
                    }}
                >
                    <StepChangeSelector
                        selectedStep={selectedStep}
                        setSelectedStep={setSelectedStep}
                        stepOptions={stepOptions}
                        disabled={disabled}
                        buttonContent={buttonContent}
                        previousTooltipText={`Reduce ${tooltipSubject}`}
                        nextTooltipText={`Increase ${tooltipSubject}`}
                        selectorWidth={selectorWidth}
                        fontSize="14px"
                        onChange={handleStepChange}
                        forceUsePopupForStepChangeSelector={true}
                    />
                </Box>
            </Box>
        </Stack>
    );
}
